import React, { useState, useCallback, useEffect } from 'react';
import { useQuery, useMutation } from 'react-apollo';
import { useParams, Link } from 'react-router-dom';
import { FIND_BY_ID_VENDA, FIND_CONFIGURACAO_UNIDADE_COMPLETA } from '../../graphql/queryes';
import { UPDATE_CONFIGURACAO_UNIDADE } from '../../graphql/mutations';
import { withBaseLayout } from '../../layout/base-layout';
import styled from '@emotion/styled';
import { Tabs, Tab } from '../../components/tabs';
import { Sistema } from './tabs/sistema';
import { Financeiro } from './tabs/financeiro';
import { Whitelabel } from './tabs/whitelabel';
import Button from '../../components/button';
import { VISUALIZAR_VENDA } from '../../router/names';
import Notification from '../../components/notification/Notification';

const TABS = {
  SISTEMA: 'Sistema',
  FINANCEIRO: 'Financeiro',
  WHITELABEL: 'Whitelabel',
};

const ConfiguracaoUnidadePage = withBaseLayout(({ venda }) => {
  const [activeTab, setActiveTab] = React.useState(TABS.SISTEMA);
  const vendaId = venda?.id;

  const [configuracaoUnidade, setConfiguracaoUnidade] = useState({});
  const [originalConfiguracaoUnidade, setOriginalConfiguracaoUnidade] = useState({});

  const [notification, setNotification] = useState({ isOpen: false, variant: '', message: '' });

  const [updateConfiguracaoUnidade] = useMutation(UPDATE_CONFIGURACAO_UNIDADE);

  const { data: configData, loading: loadingConfig } = useQuery(
    FIND_CONFIGURACAO_UNIDADE_COMPLETA,
    {
      fetchPolicy: 'no-cache',
      variables: { unidadeId: venda?.unidade?.id },
      skip: !venda?.unidade?.id,
    }
  );

  useEffect(() => {
    if (configData?.findByUnidadeConfiguracaoUnidade) {
      const dados = configData.findByUnidadeConfiguracaoUnidade;
      setConfiguracaoUnidade({ ...dados });
      setOriginalConfiguracaoUnidade({ ...dados });
    }
  }, [configData]);

  const handleChange = useCallback((field, value) => {
    setConfiguracaoUnidade((prev) => ({
      ...prev,
      [field]: value,
    }));
  }, []);

  const handleCancel = () => {
    setConfiguracaoUnidade({ ...originalConfiguracaoUnidade });
  };

  const handleSave = async () => {
    try {
      const { __typename, unidade, camposSujeitoAtencao, ...dados } = configuracaoUnidade;

      await updateConfiguracaoUnidade({
        variables: {
          configuracaoUnidade: {
            ...dados,
            ...(camposSujeitoAtencao && {
              camposSujeitoAtencao: camposSujeitoAtencao.map(({ __typename, ...campo }) => campo),
            }),
            unidade: { id: venda?.unidade?.id },
          },
        },
      });

      setOriginalConfiguracaoUnidade({ ...configuracaoUnidade });
      setNotification({ isOpen: true, variant: 'success', message: 'Configurações salvas com sucesso.' });
    } catch (error) {
      setNotification({ isOpen: true, variant: 'error', message: 'Erro ao salvar configurações.' });
    }
  };

  const renderTabContent = () => {
    switch (activeTab) {
      case TABS.SISTEMA:
        return (
          <Sistema
            venda={venda}
            configuracaoUnidade={configuracaoUnidade}
            onChange={handleChange}
          />
        );
      case TABS.FINANCEIRO:
        return (
          <Financeiro
            venda={venda}
            configuracaoUnidade={configuracaoUnidade}
            onChange={handleChange}
          />
        );
      case TABS.WHITELABEL:
        return (
          <Whitelabel
            configuracaoUnidade={configuracaoUnidade}
            onChange={handleChange}
          />
        );
      default:
        return null;
    }
  };

  if (loadingConfig) return null;

  return (
    <>
      <Notification
        close={() => setNotification({ isOpen: false, variant: '', message: '' })}
        reset={() => setNotification({ isOpen: false, variant: '', message: '' })}
        isOpen={notification.isOpen}
        variant={notification.variant}
        message={notification.message}
      />

      <div style={{ marginTop: 24 }}>
        <Link to={VISUALIZAR_VENDA.replace(':vendaId', vendaId)} style={{ textDecoration: 'none' }}>
          <Button>Voltar para a venda</Button>
        </Link>
      </div>

      <Container>
        <Tabs onChange={(tab) => {
          if (tab === TABS.FINANCEIRO && !configuracaoUnidade?.azulControleAtivado) return;
          setActiveTab(tab);
        }} value={activeTab}>
          {Object.values(TABS).map((tab) => (
            <Tab
              key={tab}
              value={tab}
              style={{
                flex: 1,
                ...(tab === TABS.FINANCEIRO && !configuracaoUnidade?.azulControleAtivado && {
                  opacity: 0.5,
                  cursor: 'not-allowed',
                }),
              }}
            >
              {tab}
            </Tab>
          ))}
        </Tabs>

        <Content>{renderTabContent()}</Content>
      </Container>

      <FloatingButtons>
        <CancelButton onClick={handleCancel}>Cancelar</CancelButton>
        <SaveButton onClick={handleSave}>Salvar</SaveButton>
      </FloatingButtons>
    </>
  );
});

const ConfiguracaoUnidade = () => {
  const { vendaId } = useParams();

  const { data, loading } = useQuery(FIND_BY_ID_VENDA, {
    fetchPolicy: 'no-cache',
    variables: { id: vendaId },
  });

  if (loading) return null;

  const venda = data?.findByIdVenda;

  return <ConfiguracaoUnidadePage venda={venda} />;
};

export default ConfiguracaoUnidade;

const Container = styled.div`
  background-color: white;
  padding: 24px;
  margin-top: 24px;
  border-radius: 8px;
  border: 1px solid #e0e0e0;
`;

const Content = styled.div`
  margin-top: 16px;
`;

const FloatingButtons = styled.div`
  position: fixed;
  bottom: 24px;
  right: 24px;
  display: flex;
  gap: 8px;
  z-index: 1000;
`;

const CancelButton = styled.button`
  padding: 10px 24px;
  border-radius: 8px;
  border: 1px solid #e0e0e0;
  background-color: #fff;
  color: #505050;
  font-weight: 600;
  font-size: 14px;
  cursor: pointer;

  &:hover {
    background-color: #f5f5f5;
  }
`;

const SaveButton = styled.button`
  padding: 10px 24px;
  border-radius: 8px;
  border: none;
  background-color: #00ACA9;
  color: #fff;
  font-weight: 600;
  font-size: 14px;
  cursor: pointer;

  &:hover {
    opacity: 0.9;
  }
`;
